import { useMutation } from "@apollo/client";
import { Button, Step, StepLabel, Stepper } from "@material-ui/core";
import clsx from "clsx";
import { QuitButton } from "components/Back";
import CancellationDialog from "components/CancelDialog/DialogCancellation";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import {
  checkReccurenceIntervalDate,
  formatVehiclesSelected,
  isVehicleTruckChecked,
  NewRequestQuoteContext,
} from "contexts/RequestQuoteContext";
import { createRequest, UPDATE_REQUEST } from "domains/Request/graphql/mutation";
import { GetClientRequests } from "domains/Request/graphql/queries";
import { ColorlibConnector } from "domains/Task/components/Job/JobLeftSide";
import { AddRequestInput, ERequestType, UpdateRequestInput } from "generated/graphql";
import { useCurrentClient } from "hooks";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer } from "ui/Drawer";
import { DoubleArrowIcon } from "ui/Icons";
import { Loader } from "ui/Loader";
import { isSuccess } from "utils/https";
import ValidateDialog from "../SamaPapser/ValidateDialog";
import VehicleType from "../SamaPapser/VehicleType";
import {
  ClickableInformationsTerms,
  ContainerBottomForm,
  ContainerButtonsStep,
  ContainerFormStep,
  FormStep,
  HeaderContainer,
  InformationsTerms,
  MainContainer,
  RootNTContainer,
  StepperContainer,
  TitleContainer,
  TitleStep,
} from "../shared/MainStyles";
import NTSideDetails from "../shared/NTSideRecap";
import { formatProduct, isFieldWithValue } from "../shared/utils";
import RecapNt from "./RecapNt";
import ReschedulingNT from "./ReschedulingNT";
import { useNTStyles } from "./styles";
import Shipment from "./Shipment";

export default function NationalTransportForm({ ...props }) {
  const styles = useNTStyles();
  const [openDialog, setOpenCancelDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const navigation = useNavigate();
  const { requestQuoteNT, setNextScreen, isEditedNT, setFieldError, fieldError, truckType, setRequestQuoteNT } =
    useContext(NewRequestQuoteContext);
  const dispatchAlert = useAlertDispatch();
  const { text } = useText();
  const steps = [`${text.all.shipment}`, `${text.all.vehicle}`, `${text.all.scheduling}`, `${text.all.recap}`];
  const [addRequestNTForClient] = useMutation(createRequest);
  const [updateRequestNT] = useMutation(UPDATE_REQUEST);
  const { client } = useCurrentClient();
  const [confirm, setConfirm] = React.useState(false);
  const [notifBadge] = React.useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!requestQuoteNT) {
      setRequestQuoteNT({ ...requestQuoteNT, type: ERequestType.Nt, stops: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderStep = () => (
    <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />} className={styles.step}>
      {steps.map((label, index) => (
        <Step
          className={clsx({
            activeStep: activeStep === index,
          })}
          key={label}
          onClick={() => null}
        >
          <StepLabel className={styles.stepLabelStyled}>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  const ViewStep = React.useMemo(() => {
    switch (activeStep) {
      case 0:
        return <Shipment />;
      case 1:
        return <VehicleType />;
      case 2:
        return <ReschedulingNT />;
      case 3:
        return <RecapNt />;
      default:
        return null;
    }
  }, [activeStep]);

  const handleBackCancel = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      setOpenCancelDialog(true);
    }
  };

  const formatStops = () =>
    requestQuoteNT.stops.map((stop, index) => ({
      action: stop.action,
      address: { 
        place_id: stop.address.place_id,
        address: stop.address.address
      },
      itemsToPick: stop.itemsToPick?.flatMap((it) => ({ counter: it.counter, product: formatProduct(it.product) })),
      itemsToDelivered: stop.itemsToDelivered?.flatMap((it) => ({
        counter: it.counter,
        product: formatProduct(it.product),
      })),
      position: index,
    }));

  const addRequestNT = () => {
    setIsLoading(true);
    try {
      const request: AddRequestInput = {
        type: requestQuoteNT.type,
        vehicles: isVehicleTruckChecked(requestQuoteNT?.vehicles)
          ? formatVehiclesSelected(requestQuoteNT?.vehicles, truckType)
          : requestQuoteNT?.vehicles,
        clientId: client?._id,
        slotStart: requestQuoteNT.slotStart,
        slotEnd: requestQuoteNT.slotEnd,
        reccurenceType: requestQuoteNT.reccurenceType,
        reccurenceOptions: requestQuoteNT.reccurenceOptions,
        productType: requestQuoteNT.productType,
        additionnalInfos: requestQuoteNT.additionnalInfos,
        startDate: requestQuoteNT.startDate,
        endDate: requestQuoteNT.endDate || requestQuoteNT.startDate,
        templateStops: formatStops(),
      };

      addRequestNTForClient({
        variables: {
          input: { request },
        },
        refetchQueries: [
          {
            query: GetClientRequests,
            variables: {
              queries: {
                id: client?._id,
                page: 1,
                limit: 9,
                date: "",
                status: [],
              },
            },
          },
        ],
      })
        .then((response) => {
          setIsLoading(false);
          const {
            data: { addRequest },
          } = response;
          if (addRequest?.code === 200) {
            dispatchAlert({
              message: text.requestQuote.successfully_created,
              severity: "success",
            });
            setRequestQuoteNT(null);
            navigation("/tracking/request");
          }
          else {
            return dispatchAlert({
              message: addRequest?.message || "",
              severity: "error",
            });
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
  };

  const updateRequestNTByClient = () => {
    setIsLoading(true);
    try {
      const request: UpdateRequestInput = {
        vehicles: isVehicleTruckChecked(requestQuoteNT?.vehicles)
          ? formatVehiclesSelected(requestQuoteNT?.vehicles, truckType)
          : requestQuoteNT?.vehicles,
        clientId: client?._id,
        slotStart: requestQuoteNT.slotStart,
        slotEnd: requestQuoteNT.slotEnd,
        reccurenceType: requestQuoteNT.reccurenceType,
        reccurenceOptions: requestQuoteNT.reccurenceOptions,
        productType: requestQuoteNT.productType,
        additionnalInfos: requestQuoteNT.additionnalInfos,
        startDate: requestQuoteNT.startDate,
        endDate: requestQuoteNT.endDate || requestQuoteNT.startDate,
        templateStops: formatStops(),
      };

      updateRequestNT({
        variables: {
          input: { id: requestQuoteNT?._id, request },
        },
        refetchQueries: [
          {
            query: GetClientRequests,
            variables: {
              queries: {
                id: client?._id,
                page: 1,
                limit: 9,
                date: "",
                status: [],
              },
            },
          },
        ],
      })
        .then(({ data }) => {
          setIsLoading(false);
          if (isSuccess(data)) {
            dispatchAlert({
              message: data.results.message,
              severity: "success",
            });
            setRequestQuoteNT(null);
            navigation("/tracking/request");
          } else {
            return dispatchAlert({
              message: data.results.message || "",
              severity: "error",
            });
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
  };

  const validateProductCounts = () => {
    const productCounts = requestQuoteNT.stops.reduce((map, point) => {
      (point.itemsToPick || []).reduce((map, item) => {
        map.set(item.product._id, (map.get(item.product._id) || 0) + item.counter);
        return map;
      }, map);
      (point.itemsToDelivered || []).reduce((map, item) => {
        map.set(item.product._id, (map.get(item.product._id) || 0) - item.counter);
        return map;
      }, map);
      return map;
    }, new Map<string, number>());

    const res = Array.from(productCounts.values()).every((count) => count === 0);
    return res;
  };

  const shipmentValidator = () => requestQuoteNT?.stops?.length && validateProductCounts();

  const checkVehicleType = () => {
    const isInvalid =
      !requestQuoteNT?.vehicles?.length ||
      (isVehicleTruckChecked(requestQuoteNT?.vehicles) && !isFieldWithValue(truckType));
    setFieldError(isInvalid);
    return !isInvalid;
  };

  const isReccurenceValuesValid = () => {
    const isInvalid = !requestQuoteNT?.productType && !checkReccurenceIntervalDate(requestQuoteNT);
    setFieldError(isInvalid);
    return !isInvalid;
  };

  const handleNext = () => {
    let isValid = false;
    switch (activeStep) {
      case 0:
        isValid = !isEditedNT ? shipmentValidator() : true;
        setFieldError(!isValid);
        if (isValid) {
          setActiveStep(activeStep + 1);
          setNextScreen(true);
        } else
          dispatchAlert({
            message: text.requestQuote.points_validator_error,
            severity: "error",
          });
        break;
      case 1:
        isValid = checkVehicleType();
        setFieldError(!isValid);
        if (isValid) {
          setActiveStep(activeStep + 1);
        }
        break;
      case 2:
        isValid = isReccurenceValuesValid();
        setFieldError(!isValid);
        if (isValid) {
          setActiveStep(activeStep + 1);
        }
        break;
      case 3:
        setConfirm(true);
        break;
      default:
        break;
    }
  };

  const handleOpenModal = () => {
    setOpenCancelDialog(true);
  };
  const handleCloseModal = () => {
    setOpenCancelDialog(false);
  };
  const onConfirmCancel = () => {
    navigation("/");
  };

  return (
    <MainContainer>
      <RootNTContainer>
        <StepperContainer>
          <HeaderContainer>
            <QuitButton onClick={handleOpenModal} />
            <TitleContainer>{text?.requestQuote.national_transport}</TitleContainer>
            {activeStep !== 3 ? (
              <Drawer
                value="right"
                initRightValue={false}
                notif={notifBadge}
                title={text.requestQuote.request_details}
                name={text.all.details}
                icon={
                  <div className={styles.iconStyleNT}>
                    <DoubleArrowIcon />
                  </div>
                }
                content={<NTSideDetails />}
              />
            ) : (
              <></>
            )}
          </HeaderContainer>
          {renderStep()}
        </StepperContainer>
        <ContainerFormStep>
          <FormStep className={styles.formStep}>
            <TitleStep>
              {(activeStep === 0 && text.all.titleStep) ||
                (activeStep === 1 && text.all.titleStep_2) ||
                (activeStep === 2 && text.all.titleStep_3)}
            </TitleStep>
            {isLoading ? <Loader /> : ViewStep}
          </FormStep>
        </ContainerFormStep>
        {!isLoading && (
          <ContainerBottomForm>
            {activeStep === 3 && (
              <InformationsTerms>
                {text.requestQuote.terms_group_1}{" "}
                <ClickableInformationsTerms>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.sec.gouv.sn/sites/default/files/CARTE%20ADMIN%20SEN%2019-02-2018_0.pdf"
                    style={{ width: "87%" }}
                  >
                    {text.requestQuote.terms_group_clickable}
                  </a>
                </ClickableInformationsTerms>{" "}
                {text.requestQuote.terms_group_final}
              </InformationsTerms>
            )}
            <ContainerButtonsStep>
              {activeStep > 0 && (
                <Button className={styles.btnPrev} color="primary" variant="outlined" onClick={handleBackCancel}>
                  {activeStep > 0 ? text.all.previous : text.all.cancel}
                </Button>
              )}
              <Button
                className={styles.btnNext}
                color="primary"
                variant="contained"
                disabled={activeStep === 0? fieldError: false}
                onClick={handleNext}
                style={{ marginLeft: activeStep > 0 ? "24px" : "auto" }}
              >
                {activeStep === 3 ? text.all.confirm : text.all.next}
              </Button>
            </ContainerButtonsStep>
          </ContainerBottomForm>
        )}
      </RootNTContainer>
      <CancellationDialog
        title={text.requestQuote.canceling_modal}
        message={text.requestQuote.canceling_confirmation_message}
        open={openDialog}
        onClose={handleCloseModal}
        onConfirm={onConfirmCancel}
      />

      <ValidateDialog
        handleCancel={onConfirmCancel}
        onClose={() => setConfirm(false)}
        onConfirm={isEditedNT ? updateRequestNTByClient : addRequestNT}
        open={confirm}
      />
    </MainContainer>
  );
}
